import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box, Button, Card, CardContent, CardHeader, Chip, Collapse, Divider, Grid, IconButton,
  Link, MenuItem, Stack, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination,
  TableRow, TableSortLabel, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { useSnackbar } from 'notistack'
import Loader from '../../common/Loader'
import MerchantSelect from './MerchantSelect'
import { Composition, ContributionLegend, HeadExplain } from './PrismContributions'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../redux/commonApiCalls'
import {
  GLOBAL_SCOPE, fetchKnobs, fetchPrismIedd, fetchPrismShadowRecords, fetchPrismShadowSummary,
  merchantScope,
} from './decisionConfigService'

// Prozo product palette (PRISM - IEDD/PRISM_UIUX_Prototype.html) — keeps this
// page visually part of PRISM rather than generic admin grey.
const BLUE = '#1A3C6E'
const TEAL = '#00A3A6'
const GREEN = '#1D7A4E'
const AMBER = '#B45309'
const RED = '#991B1B'

// The promise ladder PRISM returns on every shipment. `p98` is always the SLA
// number; whichever rung `prism_display_quantile` names is what the buyer saw.
const RUNGS = ['p50', 'p90', 'p95', 'p98']
const RUNG_Q = { p50: 0.5, p90: 0.9, p95: 0.95, p98: 0.98 }
// PRISM's catalog default for prism_display_quantile (prism/assemble.py)
const PRISM_DEFAULT_DISPLAY_Q = 0.9

// On-time floors PRISM's promise is judged against. Amazon requires 90% for
// standard seller-fulfilled (95% recommended) and 93.5% for Seller Fulfilled
// Prime, so these are the bands that decide whether a rung is shippable.
const FLOOR_SFP = 0.935
const FLOOR_RECOMMENDED = 0.95

const TIER_COLOR = { HIGH: 'success', MEDIUM: 'warning', LOW: 'default' }
const SORTABLE = {
  awb: 'AWB', merchant_id: 'Merchant', prozo_id: 'Courier', shown_edd: 'Shown EDD',
  iedd_date: 'PRISM iEDD', delivered_at: 'Delivered', confidence_tier: 'Tier', status: 'Status',
}
const COLS = 14

const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`)
const dstr = (v) => (v == null ? '—' : String(v).slice(0, 10))
const signedDays = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}d`)
const rungLabel = (r) => r.toUpperCase()

// Colour an on-time rate by the floors above. Used for both the ladder
// scoreboard and the promise cards so one number never means two things.
function rateColor(rate) {
  if (rate == null) return 'text.secondary'
  if (rate >= FLOOR_RECOMMENDED) return GREEN
  if (rate >= FLOOR_SFP) return TEAL
  if (rate >= 0.9) return AMBER
  return RED
}
function rateVerdict(rate) {
  if (rate == null) return 'not enough delivered shipments to score'
  if (rate >= FLOOR_RECOMMENDED) return 'clears every marketplace floor (incl. Amazon 95% recommended)'
  if (rate >= FLOOR_SFP) return 'clears Seller Fulfilled Prime (93.5%) but under the 95% recommendation'
  if (rate >= 0.9) return 'clears the 90% seller-fulfilled minimum only — fails SFP (93.5%)'
  return 'below the 90% marketplace minimum — listings at risk'
}

function KpiTile({ label, value, hint, color = BLUE }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderTop: `3px solid ${color}` }}>
      <CardContent sx={{ py: 1.5 }}>
        <Typography variant="caption" color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10.5 }}>
          {label}
        </Typography>
        <Typography variant="h5" sx={{ color, fontWeight: 700, lineHeight: 1.2 }}>{value}</Typography>
        {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
      </CardContent>
    </Card>
  )
}

/**
 * Per-rung scoreboard — what each quantile WOULD have scored on exactly these
 * shipments (PRISM's summary.by_quantile). This is the point of the ladder: the
 * display-quantile knob becomes a decision with numbers behind it instead of a
 * guess, and you can see the reliability/tightness trade before moving anyone.
 */
function LadderScoreboard({ byQuantile, configured }) {
  const rungs = RUNGS.filter((r) => byQuantile?.[r])
  if (!rungs.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        No scored ladder yet — rungs appear once shipments in this window have been delivered.
        (Shipments promised before the ladder shipped carry only the displayed date.)
      </Typography>
    )
  }
  return (
    <Grid container spacing={1.5}>
      {rungs.map((r) => {
        const s = byQuantile[r]
        const isConfigured = configured != null && Math.abs(RUNG_Q[r] - configured) < 1e-9
        const color = rateColor(s.on_time_rate)
        return (
          <Grid item xs={6} md={3} key={r}>
            <Card variant="outlined"
              sx={{
                height: '100%',
                borderColor: isConfigured ? TEAL : undefined,
                borderWidth: isConfigured ? 2 : 1,
                bgcolor: isConfigured ? 'rgba(0,163,166,0.05)' : undefined,
              }}>
              <CardContent sx={{ py: 1.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BLUE }}>
                    {rungLabel(r)}
                  </Typography>
                  {isConfigured && <Chip size="small" label="SHOWN" sx={{ bgcolor: TEAL, color: '#fff', height: 18, fontSize: 10 }} />}
                  {!isConfigured && r === 'p95' && <Chip size="small" variant="outlined" label="REC" sx={{ height: 18, fontSize: 10 }} />}
                </Stack>
                <Tooltip title={rateVerdict(s.on_time_rate)}>
                  <Typography variant="h6" sx={{ color, fontWeight: 700, cursor: 'help' }}>
                    {pct(s.on_time_rate)}
                  </Typography>
                </Tooltip>
                <Typography variant="caption" color="text.secondary" display="block">
                  on-time · {s.scored ?? 0} scored
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  slack {s.mean_slack_days == null ? '—' : `${s.mean_slack_days}d`}
                  {s.avg_days_late ? ` · late by ${s.avg_days_late}d` : ''}
                </Typography>
                {r === 'p50' && (
                  <Typography variant="caption" sx={{ color: AMBER, display: 'block', mt: 0.5 }}>
                    uncalibrated — ops signal only
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )
      })}
    </Grid>
  )
}

// One promise column (Shown EDD or PRISM iEDD) judged against actual delivery.
// The target band follows the CONFIGURED display quantile — judging a P90
// merchant against a P98 band would flag a correctly-calibrated 92% as a defect.
function PromiseCard({ title, stats, highlight, quantile }) {
  const s = stats || {}
  let hint
  let color
  if (quantile != null && s.on_time_rate != null) {
    const lo = quantile - 0.005
    const hi = quantile + 0.015
    const band = `calibration target ${(lo * 100).toFixed(1)}–${(hi * 100).toFixed(1)}%`
    if (s.on_time_rate > hi) {
      color = AMBER
      hint = `above the band — promises are wider than this quantile needs, which costs conversion (${band})`
    } else if (s.on_time_rate < lo) {
      color = RED
      hint = `below the band — breaking the promise more often than P${Math.round(quantile * 100)} implies (${band})`
    } else {
      color = GREEN
      hint = `within ${band}`
    }
  }
  return (
    <Card variant="outlined" sx={highlight ? { borderColor: TEAL, borderWidth: 2 } : undefined}>
      <CardContent sx={{ py: 1.5 }}>
        <Typography variant="subtitle2" sx={{ color: BLUE, fontWeight: 700 }}>{title}</Typography>
        <Typography variant="caption" color="text.secondary">
          {s.scored ?? 0} delivered &amp; scored
          {quantile != null ? ` · judged against P${Math.round(quantile * 100)}` : ''}
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap' }}>
          <Metric label="on-time" value={pct(s.on_time_rate)} color={color} hint={hint} />
          <Metric label="breach" value={pct(s.breach_rate)} color={s.breach_rate ? RED : undefined} />
          <Metric label="avg late" value={s.avg_days_late == null ? '—' : `${s.avg_days_late}d`} />
          <Metric label="slack" value={s.mean_slack_days == null ? '—' : `${s.mean_slack_days}d`}
            hint="promise − actual; big slack = padded promise" />
        </Stack>
      </CardContent>
    </Card>
  )
}

function Metric({ label, value, hint, color }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, color: color || 'text.primary' }}>{value}</Typography>
      <Tooltip title={hint || ''}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </Tooltip>
    </Box>
  )
}

// days-off vs a promise: <=0 on-time (green), >0 late/breach (red).
function DaysOffCell({ v }) {
  if (v == null) return <Typography variant="caption" color="text.secondary">—</Typography>
  const late = v > 0
  return (
    <Typography variant="caption" sx={{ color: late ? RED : GREEN, fontWeight: late ? 700 : 400 }}>
      {signedDays(v)}
    </Typography>
  )
}

// One ladder rung in the grid: the date, tinted by whether it would have held.
// The configured rung gets a ring so the eye lands on what the buyer actually saw.
function RungCell({ row, rung, configured }) {
  const date = row.iedd_ladder?.[rung]
  if (!date) return <Typography variant="caption" color="text.secondary">—</Typography>
  const hit = row.ladder_hit?.[rung]
  const isConfigured = configured != null && Math.abs(RUNG_Q[rung] - configured) < 1e-9
  const off = row.ladder_days_off?.[rung]
  return (
    <Tooltip title={hit == null ? 'not delivered yet' : `${signedDays(off)} vs actual delivery`}>
      <Box sx={{
        display: 'inline-block', px: 0.75, py: 0.25, borderRadius: 1,
        border: isConfigured ? `1.5px solid ${TEAL}` : '1.5px solid transparent',
        bgcolor: hit == null ? 'transparent' : hit ? 'rgba(29,122,78,0.10)' : 'rgba(153,27,27,0.10)',
      }}>
        <Typography variant="caption"
          sx={{ color: hit == null ? 'text.secondary' : hit ? GREEN : RED, fontWeight: isConfigured ? 700 : 400 }}>
          {String(date).slice(5)}
        </Typography>
      </Box>
    </Tooltip>
  )
}

const ATTRIBUTION_HELP = (
  <Box sx={{ maxWidth: 340 }}>
    <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
      <b>Attribution</b> — which tier of 90-day delivery history PRISM had to build the iEDD
      (it drives the confidence tier):
    </Typography>
    <Typography variant="caption" component="div">• <b>courier_code</b> — rich lane history (≥100 recs) → HIGH</Typography>
    <Typography variant="caption" component="div">• <b>mother_service</b> — moderate lane (≥30) or deep courier history → MEDIUM</Typography>
    <Typography variant="caption" component="div">• <b>service_type</b> — only thin history available</Typography>
    <Typography variant="caption" component="div">• <b>fallback</b> — no history → LOW; rate-card TAT + buffer. <b>No ladder is published here</b>: the model heads behind it are the ones PRISM declined to trust.</Typography>
  </Box>
)

/**
 * PRISM iEDD — shadow comparison. PRISM's promise ladder (P50/P90/P95/P98) vs the
 * buyer-shown EDD, judged against actual delivery. Read-only (proship /api/prism
 * proxy → PRISM :8098). Sorting and filtering are server-side, so they act on the
 * whole result set rather than the fetched page.
 */
export default function PrismTab() {
  const { enqueueSnackbar } = useSnackbar()
  const [days, setDays] = useState(7)
  const [merchant, setMerchant] = useState(null)
  const [summary, setSummary] = useState(null)
  const [configuredQ, setConfiguredQ] = useState(null)
  const [loading, setLoading] = useState(false)

  const [records, setRecords] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [status, setStatus] = useState('DELIVERED')
  const [tier, setTier] = useState('')
  const [breachedOnly, setBreachedOnly] = useState(false)
  const [sort, setSort] = useState('updated_at')
  const [order, setOrder] = useState('desc')
  const [search, setSearch] = useState('')
  const [dataLoading, setDataLoading] = useState(false)
  const [openAwb, setOpenAwb] = useState(null)
  const [detail, setDetail] = useState({})

  const [awb, setAwb] = useState('')
  const [shipment, setShipment] = useState(null)

  const { data: merchantList = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const merchantById = useMemo(() => {
    const map = {}
    merchantList.forEach((m) => { if (m.merchantId != null) map[String(m.merchantId)] = m })
    return map
  }, [merchantList])
  const merchantLabel = (mid) => {
    const m = merchantById[String(mid)]
    if (m) return m.companyName ? `${m.name} (${m.companyName})` : m.name
    return mid || '—'
  }

  // Which rung this scope actually shows a buyer. Read from the same knob store
  // the engine reads, so the page judges the promise by the merchant's own
  // configuration instead of assuming P98. /knobs returns only the scope's own
  // rows, so a merchant without an override inherits the global value, and
  // with no row anywhere the engine's catalog default (P90) applies.
  const loadConfiguredQuantile = useCallback((m) => {
    const ownQ = (res) => {
      const row = (res.data || []).find((k) => k.key === 'prism_display_quantile')
      return row?.value != null ? Number(row.value) : null
    }
    const scopes = m?.merchantId != null ? [merchantScope(m.merchantId), GLOBAL_SCOPE] : [GLOBAL_SCOPE]
    Promise.all(scopes.map((s) => fetchKnobs(s)))
      .then((results) => {
        const q = results.map(ownQ).find((v) => v != null)
        setConfiguredQ(q != null ? q : PRISM_DEFAULT_DISPLAY_Q)
      })
      .catch(() => setConfiguredQ(null))
  }, [])

  const loadSummary = useCallback((d, m) => {
    setLoading(true)
    fetchPrismShadowSummary(d, m?.merchantId ?? null)
      .then((res) => setSummary(res.data))
      .catch(() => enqueueSnackbar('Failed to load PRISM shadow summary', { variant: 'error' }))
      .finally(() => setLoading(false))
  }, [enqueueSnackbar])

  const loadRecords = useCallback((d, m, p, sz, st, ti, so, or_, br) => {
    setDataLoading(true); setOpenAwb(null)
    fetchPrismShadowRecords(d, p, sz, m?.merchantId ?? null, st, ti || undefined,
      { sort: so, order: or_, breachedOnly: br })
      .then((res) => {
        const body = res.data
        setRecords(Array.isArray(body) ? body : (body?.content || []))
        setTotal(Array.isArray(body) ? body.length : (body?.total ?? 0))
      })
      .catch(() => enqueueSnackbar('Failed to load PRISM records', { variant: 'error' }))
      .finally(() => setDataLoading(false))
  }, [enqueueSnackbar])

  const reloadAll = (over = {}) => {
    const d = over.days ?? days
    const m = 'merchant' in over ? over.merchant : merchant
    const st = over.status ?? status
    const ti = over.tier ?? tier
    const so = over.sort ?? sort
    const or_ = over.order ?? order
    const br = over.breachedOnly ?? breachedOnly
    setPage(0)
    loadSummary(d, m)
    loadConfiguredQuantile(m)
    loadRecords(d, m, 0, rowsPerPage, st, ti, so, or_, br)
  }
  useEffect(() => { reloadAll({}) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onSort = (col) => {
    const nextOrder = sort === col && order === 'desc' ? 'asc' : 'desc'
    setSort(col); setOrder(nextOrder)
    setPage(0)
    loadRecords(days, merchant, 0, rowsPerPage, status, tier, col, nextOrder, breachedOnly)
  }

  const resetFilters = () => {
    setStatus('DELIVERED'); setTier(''); setBreachedOnly(false); setSearch('')
    setSort('updated_at'); setOrder('desc')
    reloadAll({ status: 'DELIVERED', tier: '', breachedOnly: false, sort: 'updated_at', order: 'desc' })
  }

  const toggleRow = (r) => {
    if (openAwb === r.awb) { setOpenAwb(null); return }
    setOpenAwb(r.awb)
    if (!detail[r.awb]) {
      fetchPrismIedd(r.awb)
        .then((res) => setDetail((prev) => ({ ...prev, [r.awb]: res.data })))
        .catch(() => { /* panel shows a note */ })
    }
  }

  const lookup = () => {
    if (!awb.trim()) return
    setLoading(true); setShipment(null)
    fetchPrismIedd(awb.trim())
      .then((res) => setShipment(res.data))
      .catch((e) => enqueueSnackbar(
        e?.response?.status === 404 ? `PRISM has no record of AWB ${awb.trim()}` : 'Lookup failed',
        { variant: e?.response?.status === 404 ? 'info' : 'error' }))
      .finally(() => setLoading(false))
  }

  // Free-text narrowing of the fetched page only (AWB / courier / merchant).
  // Deliberately client-side: PRISM has no text-search index, and pretending a
  // server-wide search happened would be worse than a clearly-scoped one.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return records
    return records.filter((r) => [r.awb, r.prozo_id, merchantLabel(r.merchant_id)]
      .some((v) => String(v || '').toLowerCase().includes(q)))
  }, [records, search]) // eslint-disable-line react-hooks/exhaustive-deps

  const qLabel = configuredQ == null ? '—' : `P${Math.round(configuredQ * 100)}`

  return (
    <div>
      {(loading || dataLoading) && <Loader />}

      {/* ── Header ──────────────────────────────────────────────────── */}
      <Card sx={{ mb: 2, borderTop: `4px solid ${TEAL}` }}>
        <CardHeader
          title={(
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ color: BLUE, fontWeight: 700 }}>
                PRISM iEDD — promise ladder
              </Typography>
              <Chip size="small" label={`showing ${qLabel}`}
                sx={{ bgcolor: TEAL, color: '#fff', fontWeight: 700 }} />
            </Stack>
          )}
          subheader="Every shipment carries P50 / P90 / P95 / P98. The rung named by prism_display_quantile is what the buyer saw; P98 is always the SLA number. Read-only."
          action={(
            <Box sx={{ display: 'flex', gap: 1, minWidth: 420 }}>
              <MerchantSelect value={merchant}
                onChange={(m) => { setMerchant(m); reloadAll({ merchant: m }) }}
                label="Merchant (optional)" />
              <TextField select size="small" label="Window" value={days}
                onChange={(e) => { const d = Number(e.target.value); setDays(d); reloadAll({ days: d }) }}
                sx={{ width: 120 }}>
                {[1, 7, 14, 30].map((d) => <MenuItem key={d} value={d}>{d} days</MenuItem>)}
              </TextField>
            </Box>
          )}
        />
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <KpiTile label="Shipments tracked" value={summary?.shipments ?? '—'}
                hint={summary?.by_status && Object.entries(summary.by_status).map(([k, v]) => `${k}: ${v}`).join(' · ')} />
            </Grid>
            <Grid item xs={6} md={3}>
              <KpiTile label="Delivered (scored)" value={summary?.delivered_with_actual ?? '—'}
                hint="have an actual delivery time" color={TEAL} />
            </Grid>
            <Grid item xs={6} md={3}>
              <KpiTile label="iEDD vs shown"
                value={summary?.mean_iedd_minus_shown_days == null ? '—' : signedDays(summary.mean_iedd_minus_shown_days)}
                hint={`mean over ${summary?.scored_with_both ?? 0} · − = tighter than shown`}
                color={summary?.mean_iedd_minus_shown_days > 0 ? AMBER : GREEN} />
            </Grid>
            <Grid item xs={6} md={3}>
              <KpiTile label={`On-time at ${qLabel}`} value={pct(summary?.iedd?.on_time_rate)}
                hint={rateVerdict(summary?.iedd?.on_time_rate)}
                color={rateColor(summary?.iedd?.on_time_rate)} />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ color: BLUE, fontWeight: 700, mb: 1 }}>
            What each rung would have scored
            <Tooltip title="Same delivered shipments, scored against every rung of the ladder. This is the evidence for choosing prism_display_quantile — tighter promises convert better but breach more.">
              <InfoOutlinedIcon sx={{ fontSize: 15, ml: 0.5, verticalAlign: 'text-top', color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Typography>
          <LadderScoreboard byQuantile={summary?.by_quantile} configured={configuredQ} />

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}><PromiseCard title="Shown EDD (buyer-facing)" stats={summary?.shown} /></Grid>
            <Grid item xs={12} md={6}>
              <PromiseCard title={`PRISM iEDD (${qLabel})`} stats={summary?.iedd} highlight quantile={configuredQ} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Per-shipment grid ───────────────────────────────────────── */}
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title={<Typography variant="subtitle1" sx={{ color: BLUE, fontWeight: 700 }}>Per-shipment ladder</Typography>}
          subheader="Click an AWB to open its shipment detail; use the chevron for the promise-revision history. Sorting and the breach filter run server-side over the whole result set."
        />
        <CardContent>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <TextField size="small" label="Search page" value={search} placeholder="AWB / courier / merchant"
              onChange={(e) => setSearch(e.target.value)} sx={{ width: 230 }}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> }} />
            <TextField select size="small" label="Status" value={status} sx={{ width: 130 }}
              onChange={(e) => { setStatus(e.target.value); reloadAll({ status: e.target.value }) }}>
              {['DELIVERED', 'ACTIVE', 'CLOSED', 'ALL'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Tier" value={tier} sx={{ width: 130 }}
              onChange={(e) => { setTier(e.target.value); reloadAll({ tier: e.target.value }) }}>
              <MenuItem value="">All tiers</MenuItem>
              {['HIGH', 'MEDIUM', 'LOW'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <ToggleButtonGroup size="small" exclusive value={breachedOnly}
              onChange={(_e, v) => { if (v === null) return; setBreachedOnly(v); reloadAll({ breachedOnly: v }) }}>
              <ToggleButton value={false}>All</ToggleButton>
              <ToggleButton value sx={{ '&.Mui-selected': { bgcolor: 'rgba(153,27,27,0.12)', color: RED } }}>
                Breached only
              </ToggleButton>
            </ToggleButtonGroup>
            <Button size="small" startIcon={<RestartAltIcon />} onClick={resetFilters}>Reset</Button>
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'rgba(26,60,110,0.04)', fontWeight: 700, color: BLUE } }}>
                <TableCell padding="checkbox" />
                {['awb', 'merchant_id', 'prozo_id', 'shown_edd'].map((c) => (
                  <TableCell key={c} sortDirection={sort === c ? order : false}>
                    <TableSortLabel active={sort === c} direction={sort === c ? order : 'asc'}
                      onClick={() => onSort(c)}>{SORTABLE[c]}</TableSortLabel>
                  </TableCell>
                ))}
                {RUNGS.map((r) => (
                  <TableCell key={r} align="center" sx={{ fontSize: 11 }}>{rungLabel(r)}</TableCell>
                ))}
                <TableCell sortDirection={sort === 'delivered_at' ? order : false}>
                  <TableSortLabel active={sort === 'delivered_at'} direction={sort === 'delivered_at' ? order : 'asc'}
                    onClick={() => onSort('delivered_at')}>Delivered</TableSortLabel>
                </TableCell>
                <TableCell align="right">Shown Δ</TableCell>
                <TableCell align="right">iEDD Δ</TableCell>
                <TableCell sortDirection={sort === 'confidence_tier' ? order : false}>
                  <TableSortLabel active={sort === 'confidence_tier'} direction={sort === 'confidence_tier' ? order : 'asc'}
                    onClick={() => onSort('confidence_tier')}>Tier</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sort === 'status' ? order : false}>
                  <TableSortLabel active={sort === 'status'} direction={sort === 'status' ? order : 'asc'}
                    onClick={() => onSort('status')}>Status</TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((r) => {
                const open = openAwb === r.awb
                return (
                  <Fragment key={r.awb}>
                    <TableRow hover sx={{ '& > *': { borderBottom: open ? 'unset' : undefined } }}>
                      <TableCell padding="checkbox">
                        <IconButton size="small" onClick={() => toggleRow(r)}>
                          {open ? <KeyboardArrowUpIcon fontSize="inherit" /> : <KeyboardArrowDownIcon fontSize="inherit" />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {/* Opens the shipment detail page in a new tab, same
                            affordance as the Netra dashboards. order-detail/:id
                            resolves by AWB; Netra also passes ?id=<orderId> to
                            disambiguate duplicate AWBs, which PRISM's rows do
                            not carry — the page only needs it when an AWB maps
                            to more than one order. Row expansion stays on the
                            chevron so both actions remain reachable. */}
                        <Link href={`/order-detail/${encodeURIComponent(r.awb)}`}
                          target="_blank" rel="noopener noreferrer" underline="hover"
                          onClick={(e) => e.stopPropagation()}
                          title="Open shipment detail in a new tab"
                          sx={{ color: '#3783e7', fontWeight: 700, fontSize: 12.5,
                                fontFamily: 'monospace', cursor: 'pointer' }}>
                          {r.awb}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={r.merchant_id || ''}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>{merchantLabel(r.merchant_id)}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell><Typography variant="caption">{r.prozo_id || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{r.shown_edd || '—'}</Typography></TableCell>
                      {RUNGS.map((rung) => (
                        <TableCell key={rung} align="center" sx={{ px: 0.5 }}>
                          <RungCell row={r} rung={rung} configured={configuredQ} />
                        </TableCell>
                      ))}
                      <TableCell><Typography variant="caption">{dstr(r.delivered_at)}</Typography></TableCell>
                      <TableCell align="right"><DaysOffCell v={r.shown_days_off} /></TableCell>
                      <TableCell align="right"><DaysOffCell v={r.iedd_days_off} /></TableCell>
                      <TableCell>
                        <Chip size="small" label={r.confidence_tier || '—'} color={TIER_COLOR[r.confidence_tier] || 'default'} />
                      </TableCell>
                      <TableCell><Typography variant="caption">{r.status}</Typography></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={COLS} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <PrismDetail data={detail[r.awb]} row={r} configured={configuredQ} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                )
              })}
              {visible.length === 0 && (
                <TableRow><TableCell colSpan={COLS}>
                  <Typography variant="caption" color="text.secondary">
                    {records.length ? 'No rows match the search on this page.' : 'No PRISM shipments for this window/filter.'}
                  </Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination colSpan={COLS} count={total} page={page} rowsPerPage={rowsPerPage}
                  onPageChange={(_e, p) => { setPage(p); loadRecords(days, merchant, p, rowsPerPage, status, tier, sort, order, breachedOnly) }}
                  onRowsPerPageChange={(e) => {
                    const sz = parseInt(e.target.value, 10); setRowsPerPage(sz); setPage(0)
                    loadRecords(days, merchant, 0, sz, status, tier, sort, order, breachedOnly)
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]} />
              </TableRow>
            </TableFooter>
          </Table>
        </CardContent>
      </Card>

      {/* ── Direct AWB lookup ───────────────────────────────────────── */}
      <Card>
        <CardHeader title={<Typography variant="subtitle1" sx={{ color: BLUE, fontWeight: 700 }}>Look up an AWB</Typography>}
          subheader="Promise timeline for a specific shipment (any window)" />
        <CardContent>
          <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="AWB number" value={awb}
                onChange={(e) => setAwb(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && lookup()} />
            </Grid>
            <Grid item>
              <Button variant="contained" startIcon={<SearchIcon />} onClick={lookup}
                sx={{ bgcolor: BLUE, '&:hover': { bgcolor: '#15305a' } }}>Look up</Button>
            </Grid>
          </Grid>
          {shipment && <PrismDetail data={shipment} configured={configuredQ} />}
        </CardContent>
      </Card>
    </div>
  )
}

// Expandable "why": the ladder, attribution + confidence + NDR, then every revision.
function PrismDetail({ data, row, configured }) {
  if (!data) {
    return <Box sx={{ p: 2 }}><Typography variant="caption" color="text.secondary">Loading detail…</Typography></Box>
  }
  const history = data.history || []
  const ladder = data.iedd_ladder || row?.iedd_ladder || {}
  const rungs = RUNGS.filter((r) => ladder[r])
  return (
    <Box sx={{ p: 2, bgcolor: 'rgba(26,60,110,0.03)' }}>
      {rungs.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ color: BLUE, fontWeight: 700, mb: 0.5 }}>Promise ladder</Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            {rungs.map((r) => {
              const isConfigured = configured != null && Math.abs(RUNG_Q[r] - configured) < 1e-9
              return (
                <Box key={r} sx={{
                  px: 1.25, py: 0.5, borderRadius: 1, minWidth: 92,
                  border: `1.5px solid ${isConfigured ? TEAL : 'rgba(0,0,0,0.12)'}`,
                  bgcolor: isConfigured ? 'rgba(0,163,166,0.07)' : 'transparent',
                }}>
                  <Typography variant="caption" sx={{ color: BLUE, fontWeight: 700 }}>
                    {rungLabel(r)}{isConfigured ? ' · shown' : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{ladder[r]}</Typography>
                </Box>
              )
            })}
          </Stack>
        </>
      )}
      {rungs.length === 0 && (
        <Typography variant="caption" sx={{ color: AMBER, display: 'block', mb: 1 }}>
          No ladder on this shipment — fallback attribution or an NDR re-score, where PRISM
          publishes only the single date it is willing to stand behind.
        </Typography>
      )}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ color: BLUE, fontWeight: 700 }}>Why this iEDD</Typography>
        <Tooltip title={ATTRIBUTION_HELP} arrow>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
        </Tooltip>
      </Stack>
      <Stack direction="row" spacing={3} sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Metric label="attribution" value={data.attribution_level || '—'} hint="data support cascade — hover the ⓘ" />
        <Metric label="confidence" value={data.confidence_tier || '—'} />
        <Metric label="shown EDD" value={data.shown_edd || '—'} />
        <Metric label="iEDD (displayed)" value={data.iedd_date || '—'}
          hint={data.iedd_range_end ? `range end ${data.iedd_range_end}` : ''} />
        <Metric label="P98 (SLA)" value={data.iedd_p98 || '—'} hint="what SLA accounting scores against, whatever the knob says" />
        <Metric label="delivered" value={dstr(data.delivered_at)} />
        {data.ndr && <Metric label="NDR" value={`att ${data.ndr.attempt_number}`} color={RED}
          hint={`p(next)=${data.ndr.p_next_succeeds?.toFixed?.(2) ?? '—'}`} />}
      </Stack>
      <Composition c={data.components} />
      {data.explain && (
        <>
          <Typography variant="subtitle2" sx={{ color: BLUE, fontWeight: 700, mb: 0.5 }}>
            What moved the estimate
          </Typography>
          <HeadExplain title="Transit" ex={data.explain.transit} />
          <HeadExplain title="Warehouse" ex={data.explain.wh} />
          <HeadExplain title="NDR re-attempt" ex={data.explain.mobility} />
          <ContributionLegend />
        </>
      )}
      {!data.explain && data.components && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          No attribution recorded for this promise.
        </Typography>
      )}
      <Divider sx={{ mb: 1 }} />
      <Typography variant="subtitle2" sx={{ color: BLUE, fontWeight: 700, mb: 0.5 }}>Promise revisions</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell><TableCell>Trigger</TableCell><TableCell>iEDD</TableCell>
            <TableCell>Range end</TableCell><TableCell>Tier</TableCell><TableCell>At (UTC)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {history.map((h, i) => (
            <TableRow key={i}>
              <TableCell>{i + 1}</TableCell>
              <TableCell>{h.update_reason}</TableCell>
              <TableCell>{h.iedd_date}</TableCell>
              <TableCell>{h.iedd_range_end || '—'}</TableCell>
              <TableCell><Chip size="small" label={h.confidence_tier} color={TIER_COLOR[h.confidence_tier] || 'default'} /></TableCell>
              <TableCell><Typography variant="caption">{h.updated_at}</Typography></TableCell>
            </TableRow>
          ))}
          <TableRow sx={{ bgcolor: 'rgba(0,163,166,0.06)' }}>
            <TableCell>{history.length + 1}</TableCell>
            <TableCell>{data.update_reason} (current)</TableCell>
            <TableCell>{data.iedd_date}</TableCell>
            <TableCell>{data.iedd_range_end || '—'}</TableCell>
            <TableCell><Chip size="small" label={data.confidence_tier} color={TIER_COLOR[data.confidence_tier] || 'default'} /></TableCell>
            <TableCell><Typography variant="caption">{data.updated_at}</Typography></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  )
}
